import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ChakraProvider,
  theme,
  useToast,
} from '@chakra-ui/react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import Router from './routes';
import { AuthContextProvider } from './Context/AuthContext';
import { CartContextProvider } from './Context/CartContext';
import { CityContextProvider } from './Context/CityContext';
import { UserContextProvider } from './Context/UserContext';
import { AgeVerification } from "./components/AgeVerification";
// import { InfoPopUp } from "./components/InfoPopUp";
import { WhatsAppButton } from "./components/WhatsAppButton";
import ScrollToTop from "./components/ScrollToTop";
import { api } from './services/api';

// Global PIX payment poller — lives inside BrowserRouter so it persists across
// all page navigations. The Payment page handles its own polling while the user
// is on it; this component takes over once the user navigates away.
function PixPaymentPoller() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const inFlightRef = useRef(false);
  const intervalRef = useRef(null);

  const check = useCallback(async () => {
    const paymentId  = localStorage.getItem('pendingPaymentId');
    const userId     = localStorage.getItem('pendingPaymentUserId');
    if (!paymentId || inFlightRef.current) return;
    if (window.location.pathname.startsWith('/pagamentos/')) return;

    inFlightRef.current = true;
    try {
      const res = await api.get(`/api/v1/payments/${paymentId}/invoice_status`);
      if (res.data?.invoice?.status === 'paid') {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        localStorage.removeItem('pendingPaymentId');
        localStorage.removeItem('pendingPaymentUserId');
        toast({
          title: "Pagamento PIX confirmado!",
          description: "Seu pedido foi recebido com sucesso.",
          status: "success",
          duration: 8000,
          isClosable: true,
          position: "top",
        });
        navigate(`/perfil/${userId}`);
      }
    } catch {
      // silent — keep polling
    } finally {
      inFlightRef.current = false;
    }
  }, [navigate, toast]);

  // Fire once on every route change (catches the transition off the payment page)
  useEffect(() => { check(); }, [pathname, check]);

  // Continuous interval while off the payment page
  useEffect(() => {
    if (pathname.startsWith('/pagamentos/')) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }
    if (!localStorage.getItem('pendingPaymentId')) return;
    intervalRef.current = setInterval(check, 30000);
    return () => { clearInterval(intervalRef.current); intervalRef.current = null; };
  }, [pathname, check]);

  return null;
}

function App() {
  const [visitCount, setVisitCount] = useState(0);
  const location = window.location.pathname; // Access current URL

  function countVisits() {
    let visitCount = localStorage.getItem('visitCount');
    visitCount = visitCount ? parseInt(visitCount, 10) + 1 : 1;
    localStorage.setItem('visitCount', visitCount);
    return visitCount;
  }

  useEffect(() => {
    const currentURL = window.location.pathname;
    if (currentURL === '/produtos' || currentURL === '/') {
      const count = countVisits();
      setVisitCount(count);
    }
  }, []);

  return (
    <ChakraProvider theme={theme}>
      <AuthContextProvider>
        <UserContextProvider>
          <CityContextProvider>
            <CartContextProvider>
              {location !== '/link_boozen' && <AgeVerification />}
              {location !== '/link_boozen' && <WhatsAppButton />}
              <BrowserRouter>
                <ScrollToTop />
                <PixPaymentPoller />
                <Router />
              </BrowserRouter>
            </CartContextProvider>
          </CityContextProvider>
        </UserContextProvider>
      </AuthContextProvider>
    </ChakraProvider>
  );
}

export default App;
